// Button `special=` short-circuits — frontend-only behaviours that intercept
// a button click BEFORE it reaches the central RUN_BUTTON dispatcher.
//
// The DSL author writes `<button command="x" special="file_upload" />` to
// declare the special; the form/list code paths consult `resolveSpecial(name)`
// at click time and, when a handler is registered, route the click through
// the handler's `execute()` first. The handler returns:
//
//   - `Record<string, unknown>` → an extra payload to MERGE into the
//     RUN_BUTTON command's `context` slot before dispatch (e.g.
//     file_upload returns `{ file_content, file_name, file_mime }`).
//   - `null` → user cancelled / unrecoverable; the dispatch is SKIPPED.
//
// Specials are pure-frontend — they never reach the backend. Behaviours that
// need server cooperation (e.g. `cancel`/`discard` interactions with the
// in-progress form-state machine from WS 4.1) get their handlers wired here
// once their host substrate ships.

import type { Json } from "../contracts/json";

export interface CommandSpecialHandler {
    /** Run the special; return extra context to merge OR null to cancel. */
    execute(options: Record<string, string>): Promise<Record<string, Json> | null>;
}

const REGISTRY: Map<string, () => CommandSpecialHandler> = new Map();

export function registerSpecial(name: string, factory: () => CommandSpecialHandler): void {
    REGISTRY.set(name, factory);
}

export function resolveSpecial(name: string): CommandSpecialHandler | null {
    const factory = REGISTRY.get(name);
    return factory ? factory() : null;
}

// ─── Built-in: file_upload ─────────────────────────────────────────────────
// Opens a native file picker, reads the selected file as base64, and
// returns `{ file_content, file_name, file_mime }` for the RUN_BUTTON
// dispatcher to merge into the command's context. `accept` may be supplied
// via a `special-accept="…"` DSL attribute on the button (e.g.
// `accept=".json,application/json"`).

class FileUploadSpecial implements CommandSpecialHandler {
    execute(options: Record<string, string>): Promise<Record<string, Json> | null> {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            if (options.accept) input.accept = options.accept;
            input.addEventListener("change", () => {
                const file = input.files?.[0];
                if (!file) { resolve(null); return; }
                const reader = new FileReader();
                reader.onload = () => {
                    const dataUrl = reader.result as string;
                    const base64 = dataUrl.split(",")[1] ?? "";
                    resolve({
                        file_content: base64,
                        file_name: file.name,
                        file_mime: file.type || "application/octet-stream",
                    });
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
            });
            input.addEventListener("cancel", () => resolve(null));
            input.click();
        });
    }
}

registerSpecial("file_upload", () => new FileUploadSpecial());
